import React, { useEffect, useRef } from "react";
import { Link } from "gatsby";
import { colors } from "~/utils/colors";
import { breakPoints, fontFamily, siteWidth } from "~/utils/variables";

export interface BreadCrumb {
  name: string;
  to?: string | undefined;
}

interface Props {
  bgColor?: string;
  items?: BreadCrumb[];
  spPaddingTop?: string;
  spPaddingBottom?: string;
}

export const BreadCrumbs: React.FC<Props> = ({
  bgColor = colors.white,
  items = [],
  spPaddingTop,
  spPaddingBottom,
}) => {
  const resolvedBgColor = bgColor || colors.white;
  const ancestorsRef = useRef<HTMLDivElement>(null);

  const ancestorItems = items.slice(0, -1);
  const currentItem = items[items.length - 1];

  useEffect(() => {
    if (ancestorsRef.current) {
      ancestorsRef.current.scrollLeft = 0;
    }
  }, [items]);

  if (!items.length) {
    return null;
  }

  return (
    <>
      <style jsx>{`
        .wrapper {
          background-color: ${resolvedBgColor};
          padding: 12px 0 12px;
        }

        .inner {
          box-sizing: border-box;
          margin: 0 auto;
          max-width: ${siteWidth.default};
          min-width: 0;
          padding: 0;
          width: 100%;
        }

        .breadcrumbs {
          align-items: center;
          display: flex;
          gap: 8px;
          min-width: 0;
          overflow: hidden;
          padding: 0 30px;
          width: 100%;
        }

        .breadcrumbAncestors {
          -webkit-overflow-scrolling: touch;
          flex: 0 1 auto;
          max-width: calc(100% - 4rem);
          min-width: 0;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
        }

        .breadcrumbAncestors::-webkit-scrollbar {
          display: none;
        }

        .breadcrumbAncestorList {
          align-items: center;
          display: flex;
          flex-wrap: nowrap;
          gap: 8px;
          list-style: none;
          margin: 0;
          padding: 0;
          width: max-content;
        }

        .breadcrumbAncestorItem {
          align-items: center;
          display: flex;
          flex: 0 0 auto;
          gap: 8px;
        }

        .breadcrumbAncestorItem :global(a) {
          color: ${colors.lightText};
          display: block;
          text-decoration: none;
          transition: color 0.15s ease;
          white-space: nowrap;
        }

        .breadcrumbAncestorItem :global(a:hover),
        .breadcrumbAncestorItem :global(a:focus-visible) {
          color: ${colors.orange};
          text-decoration: underline;
        }

        .breadcrumbListItemName {
          display: block;
          font-family: ${fontFamily.default};
          font-size: 12px;
          font-weight: 400;
          letter-spacing: 0.02em;
          line-height: 1.5;
          white-space: nowrap;
        }

        .breadcrumbSeparator {
          align-items: center;
          color: ${colors.gray4};
          display: inline-flex;
          flex: 0 0 auto;
          font-family: ${fontFamily.default};
          font-size: 12px;
          font-weight: 400;
          line-height: 1.5;
        }

        .breadcrumbCurrentWrap {
          align-items: center;
          display: flex;
          flex: 1 1 0;
          gap: 8px;
          min-width: 0;
          overflow: hidden;
        }

        .breadcrumbCurrent {
          color: ${colors.defaultText};
          display: block;
          font-family: ${fontFamily.default};
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.02em;
          line-height: 1.5;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        @media (max-height: 800px) {
          .wrapper {
            padding: 8px 0 8px;
          }
        }

        @media (${breakPoints.sp}) {
          .wrapper {
            padding: ${spPaddingTop || `12px`} 0 ${spPaddingBottom || `12px`};
          }

          .breadcrumbs {
            padding: 0 20px;
          }
        }
      `}</style>
      <div className="wrapper">
        <div className="inner">
          <nav className="breadcrumbs" aria-label="ぱんくずリスト">
            {ancestorItems.length > 0 && (
              <div className="breadcrumbAncestors" ref={ancestorsRef}>
                <ol className="breadcrumbAncestorList">
                  {ancestorItems.map((item, index) => (
                    <li key={`BreadCrumbAncestor_${index}`} className="breadcrumbAncestorItem">
                      {item.to ? (
                        <Link to={item.to}>
                          <span className="breadcrumbListItemName">{item.name}</span>
                        </Link>
                      ) : (
                        <span className="breadcrumbListItemName">{item.name}</span>
                      )}
                      {index < ancestorItems.length - 1 && (
                        <span className="breadcrumbSeparator" aria-hidden="true">
                          ›
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div className="breadcrumbCurrentWrap">
              {ancestorItems.length > 0 && (
                <span className="breadcrumbSeparator" aria-hidden="true">
                  ›
                </span>
              )}
              <span className="breadcrumbCurrent" aria-current="page" title={currentItem.name}>
                {currentItem.name}
              </span>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};
